<template>
  <div ref="wrapperRef" class="m-slider-wrapper">
    <div class="m-slider-container">
      <!-- Left value display/input -->
      <InputNumber 
        v-model="sliderValue" 
        :min="min" 
        :max="max" 
        :disabled="disabled || readonly"
        class="m-slider-input"
        @blur="handleBlur"
      />

      <!-- Slider -->
      <Slider 
        v-model="sliderValue" 
        :min="min" 
        :max="max" 
        :step="step"
        :disabled="disabled || readonly"
        class="m-slider"
      />

      <!-- Right value (max) -->
      <span class="m-slider-max-label">{{ max }}</span>
    </div>

    <div v-if="hint" class="m-slider-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import InputNumber from 'primevue/inputnumber'
import Slider from 'primevue/slider'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref, useAttrs } from 'vue'

interface Props {
  modelValue?: number
  min?: number
  max?: number
  step?: number
  label?: string
  hint?: string
  disabled?: boolean
  readonly?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  min: 0,
  max: 100,
  step: 1,
  label: '',
  hint: '',
  disabled: false,
  readonly: false,
  errorMessages: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: number]
  'change': [value: number]
  'blur': []
}>()

const wrapperRef = ref<HTMLElement | null>(null)

const attrs = useAttrs()

const sliderValue = computed({
  get: () => props.modelValue || props.min,
  set: (value) => {
    emit('update:modelValue', value)
    emit('change', value)
  }
})

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const hasErrors = computed(() => normalizedErrors.value.length > 0)

const handleBlur = () => {
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-slider-wrapper {
  width: 100%;
}

.m-slider-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.m-slider-input {
  width: 80px;
  flex-shrink: 0;
}

.m-slider {
  flex: 1;
  
  :deep(.p-slider) {
    background: #e5e7eb;
    
    .p-slider-handle {
      background: var(--primary-color, #1e3a6e);
      border-color: var(--primary-color, #1e3a6e);

      &:hover {
        background: color-mix(in srgb, var(--primary-color, #1e3a6e) 92%, black);
        border-color: color-mix(in srgb, var(--primary-color, #1e3a6e) 92%, black);
      }
    }

    .p-slider-range {
      background: var(--primary-color, #1e3a6e);
    }
  }
}

.m-slider-max-label {
  min-width: 40px;
  text-align: right;
  color: #6b7280;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.m-slider-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>

